import { useCallback, useState } from "react";

import type { DialogProps } from "@frontend/components/atoms/Dialog";

export type UseDiscardGuardOptions = {
  /** Whether closing now would lose something. */
  dirty: boolean;
  /** What closing does, once it is allowed. */
  onClose: () => void;
};

export type UseDiscardGuard = {
  /** For the dialog: lets a close through, or holds it and asks. */
  onOpenChange: NonNullable<DialogProps["onOpenChange"]>;
  /** For a cancel button: the same question, without a dialog event. */
  requestClose: () => void;
  /** Whether the question is on screen. */
  confirming: boolean;
  /** The two answers. */
  keep: () => void;
  discard: () => void;
};

/**
 * Stands between a form and the ways out of it. While the form is clean, a
 * close request goes through; once it is dirty, the request is held and the
 * caller shows a question, then lets it through on `discard`.
 */
export function useDiscardGuard({
  dirty,
  onClose,
}: UseDiscardGuardOptions): UseDiscardGuard {
  const [confirming, setConfirming] = useState(false);

  const requestClose = useCallback(() => {
    if (dirty) {
      setConfirming(true);

      return;
    }

    onClose();
  }, [dirty, onClose]);

  const onOpenChange = useCallback<NonNullable<DialogProps["onOpenChange"]>>(
    (open, eventDetails) => {
      if (open) {
        return;
      }

      if (dirty) {
        // The dialog stays; the question opens over it.
        eventDetails.cancel();
        setConfirming(true);

        return;
      }

      onClose();
    },
    [dirty, onClose],
  );

  const keep = useCallback(() => setConfirming(false), []);

  const discard = useCallback(() => {
    setConfirming(false);
    onClose();
  }, [onClose]);

  return { onOpenChange, requestClose, confirming, keep, discard };
}
